/**
 * Observation Fragment
 * Drives the "Try It" console on the Event Monitoring page: it calls the pull
 * API with the key the user pastes in, renders the exchange, and carries the
 * cursor from each response into the next request.
 */

import { Events } from '../../../../js/utils/events.js';

let initialized = false;

// ============================================================================
// Rendering helpers
//
// The output is built as HTML strings, so everything that comes back from the
// network goes through esc() first.
// ============================================================================

function esc(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
}

function renderOutput(requestExplanation, url, maskedKey, responseHtml) {
    return '<div class="px-3 py-3">'
        + '<div class="ui-stack ui-vertical ui-compact">'
        + '<span class="text-xs font-semibold text-text-tertiary uppercase tracking-wide">Request</span>'
        + '<p class="text-sm text-text-secondary">' + requestExplanation
        + ' The API key is passed as a <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">Bearer</code> token in the <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">Authorization</code> header.</p>'
        + '<pre class="text-xs bg-surface-page border border-border-subtle rounded-sm p-2 overflow-x-auto"><code>'
        + 'GET ' + esc(url) + '\n'
        + 'Authorization: Bearer ' + esc(maskedKey)
        + '</code></pre>'
        + responseHtml
        + '</div></div>';
}

function formatBody(bodyText) {
    if (!bodyText.trim()) return '(empty — no new events)';
    return bodyText.trim().split('\n').map((line) => {
        try {
            return JSON.stringify(JSON.parse(line), null, 2);
        } catch (e) {
            return line;
        }
    }).join('\n\n');
}

function formatResponseHeaders(status, statusText, contentType, nextCursor, hasMore, retryAfter) {
    let h = 'HTTP ' + status + ' ' + statusText + '\nContent-Type: ' + contentType;
    if (nextCursor) h += '\nX-Next-Cursor: ' + nextCursor;
    if (hasMore !== null) h += '\nX-Has-More: ' + hasMore;
    if (retryAfter) h += '\nRetry-After: ' + retryAfter;
    return h;
}

function buildExplanation(status, nextCursor, hasMore, retryAfter) {
    let html = '';
    if (status === 200) {
        html += '<p class="text-sm text-text-secondary">Your consumer should read these response headers to manage polling:</p>';
        if (nextCursor) {
            html += '<p class="text-sm text-text-secondary">&bull; <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">X-Next-Cursor: ' + esc(nextCursor) + '</code> &mdash; store this and pass it as the <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">cursor</code> query parameter on the next request to resume from this position.</p>';
        }
        if (hasMore !== null) {
            const tip = hasMore === 'true'
                ? 'more events are available &mdash; fetch again immediately.'
                : 'no more events right now &mdash; wait and poll again later.';
            html += '<p class="text-sm text-text-secondary">&bull; <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">X-Has-More: ' + hasMore + '</code> &mdash; ' + tip + '</p>';
        }
    } else if (status === 401) {
        html += '<p class="text-sm text-red-600">Authentication failed. Check that the API key is correct and has not been revoked.</p>';
    } else if (status === 429) {
        html += '<p class="text-sm text-amber-600">Rate limited.';
        if (retryAfter) html += ' Wait ' + retryAfter + ' seconds before trying again.';
        html += '</p>';
    }
    return html;
}

// ============================================================================
// Observation Handlers
// Template contract:
//   <div data-observation-console data-endpoint="https://...">
//     <input data-observation-key>
//     <input data-observation-cursor>
//     <button data-observation-pull>
//     <div data-observation-output hidden>
// ============================================================================

const ObservationHandlers = {
    onPull(e, element) {
        const console_ = element.closest('[data-observation-console]');
        if (!console_) return;

        const keyInput = console_.querySelector('[data-observation-key]');
        const cursorInput = console_.querySelector('[data-observation-cursor]');
        const output = console_.querySelector('[data-observation-output]');
        if (!keyInput || !cursorInput || !output) return;

        const apiKey = keyInput.value.trim();
        if (!apiKey) {
            keyInput.focus();
            return;
        }

        const cursorVal = cursorInput.value.trim();
        let url = console_.dataset.endpoint;
        if (cursorVal) {
            url += '?cursor=' + encodeURIComponent(cursorVal);
        }
        const maskedKey = apiKey.substring(0, 8) + '...';

        const requestExplanation = !cursorVal
            ? 'No cursor provided &mdash; fetching events from the beginning.'
            : 'Cursor is set to <code class="text-xs bg-surface-muted px-1 py-0.5 rounded-sm">' + esc(cursorVal) + '</code> &mdash; fetching events after that position.';

        output.hidden = false;
        output.innerHTML = renderOutput(requestExplanation, url, maskedKey,
            '<p class="text-sm text-text-tertiary italic">Sending request...</p>');

        fetch(url, { headers: { Authorization: 'Bearer ' + apiKey } })
            .then((response) => {
                const nextCursor = response.headers.get('X-Next-Cursor');
                const hasMore = response.headers.get('X-Has-More');
                const contentType = response.headers.get('Content-Type') || 'N/A';
                const retryAfter = response.headers.get('Retry-After');

                // Carried into the next pull so it resumes where this left off.
                if (nextCursor) cursorInput.value = nextCursor;

                return response.text().then((bodyText) => {
                    const headerBlock = formatResponseHeaders(
                        response.status, response.statusText, contentType,
                        nextCursor, hasMore, retryAfter,
                    );
                    const responseHtml = ''
                        + '<span class="text-xs font-semibold text-text-tertiary uppercase tracking-wide">Response &mdash; ' + response.status + ' ' + esc(response.statusText) + '</span>'
                        + buildExplanation(response.status, nextCursor, hasMore, retryAfter)
                        + '<pre class="text-xs bg-surface-page border border-border-subtle rounded-sm p-2 overflow-x-auto" style="max-height:400px;overflow-y:auto"><code>'
                        + esc(headerBlock) + '\n\n' + esc(formatBody(bodyText))
                        + '</code></pre>';

                    output.innerHTML = renderOutput(requestExplanation, url, maskedKey, responseHtml);
                    // The call itself is logged, so the table below is stale now.
                    window.htmx.ajax('GET', '/app/account/observation/usage', {
                        target: '#observation-usage-table',
                        swap: 'innerHTML',
                    });
                });
            })
            .catch((err) => {
                output.innerHTML = renderOutput(requestExplanation, url, maskedKey,
                    '<span class="text-xs font-semibold text-red-500 uppercase tracking-wide">Error</span>'
                    + '<p class="text-sm text-red-600">' + esc(err.message) + '</p>');
            });
    },
};

// ============================================================================
// Initialization
// ============================================================================

export function initObservation() {
    if (initialized) return;
    initialized = true;

    Events.on('click', 'observation-pull', ObservationHandlers.onPull);
}
